import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "./DashboardLayout";
import { api, formatApiError } from "@/lib/api";
import { Plus, Trash2, ChevronRight } from "lucide-react";

const COLORS = ["#FBBF24", "#F472B6", "#38BDF8", "#A3E635", "#A78BFA", "#FB7185"];
const EMOJIS = ["🦊", "🐼", "🐨", "🐯", "🐸", "🐵", "🦁", "🐰", "🦄"];

export default function ChildrenList() {
  const [children, setChildren] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: "",
    age: 8,
    avatar_color: COLORS[0],
    avatar_emoji: EMOJIS[0],
  });
  const [err, setErr] = useState("");

  const load = async () => {
    const { data } = await api.get("/children");
    setChildren(data);
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.post("/children", { ...form, age: Number(form.age) });
      setForm({ name: "", age: 8, avatar_color: COLORS[0], avatar_emoji: EMOJIS[0] });
      setShowForm(false);
      load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover esta criança e todas as sessões dela?")) return;
    await api.delete(`/children/${id}`);
    load();
  };

  return (
    <DashboardLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl">Crianças</h1>
          <p className="text-slate-500 mt-1">Cadastre os perfis das crianças acompanhadas.</p>
        </div>
        <button
          onClick={() => setShowForm((s) => !s)}
          className="bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition flex items-center gap-2"
          data-testid="add-child-button"
        >
          <Plus size={16} /> Nova criança
        </button>
      </div>

      {showForm && (
        <form onSubmit={create} className="dash-card mb-6 space-y-4" data-testid="add-child-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Nome</label>
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500"
                data-testid="child-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Idade</label>
              <input
                type="number"
                min={3}
                max={17}
                required
                value={form.age}
                onChange={(e) => setForm({ ...form, age: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500"
                data-testid="child-age-input"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Avatar</label>
            <div className="flex flex-wrap gap-2">
              {EMOJIS.map((e) => (
                <button
                  type="button"
                  key={e}
                  onClick={() => setForm({ ...form, avatar_emoji: e })}
                  className={`w-12 h-12 text-2xl rounded-lg border-2 transition ${
                    form.avatar_emoji === e ? "border-sky-500 bg-sky-50" : "border-slate-200"
                  }`}
                  data-testid={`avatar-emoji-${e}`}
                >
                  {e}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {COLORS.map((c) => (
                <button
                  type="button"
                  key={c}
                  onClick={() => setForm({ ...form, avatar_color: c })}
                  className={`w-8 h-8 rounded-full border-2 transition ${
                    form.avatar_color === c ? "border-slate-900 scale-110" : "border-white"
                  }`}
                  style={{ background: c }}
                  data-testid={`avatar-color-${c}`}
                />
              ))}
            </div>
          </div>
          {err && <p className="text-sm text-red-600">{err}</p>}
          <div className="flex gap-2">
            <button
              type="submit"
              className="bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-4 py-2 rounded-lg"
              data-testid="child-save-button"
            >
              Salvar
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="px-4 py-2 text-sm rounded-lg border border-slate-200"
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      {children.length === 0 ? (
        <div className="dash-card text-slate-500 text-sm" data-testid="children-empty">
          Nenhuma criança cadastrada ainda.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="children-list">
          {children.map((c) => (
            <div key={c.id} className="dash-card flex items-center gap-4">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center text-3xl"
                style={{ background: c.avatar_color }}
              >
                {c.avatar_emoji}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900">{c.name}</div>
                <div className="text-xs text-slate-500">{c.age} anos · {c.coins} moedas</div>
              </div>
              <Link
                to={`/dashboard/children/${c.id}`}
                className="text-sky-600 hover:text-sky-700 text-sm font-medium flex items-center gap-1"
                data-testid={`open-child-${c.id}`}
              >
                Ver relatório <ChevronRight size={16} />
              </Link>
              <button
                onClick={() => remove(c.id)}
                className="text-slate-400 hover:text-red-500 p-1"
                aria-label="Remover"
                data-testid={`remove-child-${c.id}`}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
